import React from 'react';
import FilterOptionSubCheckbox from './filter-option-sub-checkbox';
import { FilterOptionCheckbox } from '../filter-option-checkbox-group/filter-option-checkbox';
import FilterOptionCheckboxContainer from '../filter-option-checkbox-group/filter-option-checkbox-container';
import { NumberObject } from '@/common/types/number-object';
import getFormattedLabel from '@/catalog/utils/get-formatted-label';

interface FilterOptionMainCheckboxProps {
  /** The main (or parent) filter value for this checkbox, e.g. `BMW` */
  mainFilter: string;
  mainFilterCount?: number;
  /** List of all available child filters associated with this main filter, e.g. `['X3', '330i']` */
  subFilters: string[];
  subFiltersCount?: NumberObject;
  /** Title for subfilters */
  subFiltersTitle?: string;
  /** Determines if the main checkbox is checked. */
  checked: boolean;
  /** List of currently selected child filters, e.g `[]` */
  selectedSubFilters?: string[];
  /* Fuction to toggle the parent checkbox. */
  toggleMainCheckbox: (checked: boolean, mainValue: string) => void;
  /* Fuction to toggle the child checkbox. */
  toggleSubCheckbox: (checked: boolean, mainValue: string, subValue: string) => void;
}

function FilterOptionMainCheckbox({
  mainFilter,
  mainFilterCount,
  subFilters,
  subFiltersCount,
  subFiltersTitle,
  checked,
  selectedSubFilters,
  toggleMainCheckbox,
  toggleSubCheckbox,
}: FilterOptionMainCheckboxProps) {
  const disabled = mainFilterCount !== undefined && mainFilterCount === 0;

  return (
    <FilterOptionCheckboxContainer>
      <FilterOptionCheckbox
        checked={checked}
        label={getFormattedLabel(mainFilter, mainFilterCount, disabled)}
        value={mainFilter}
        disabled={disabled}
        onClick={() => toggleMainCheckbox(checked, mainFilter)}
      />

      {checked ? (
        <FilterOptionCheckboxContainer className="pb-[24px] pl-[32px]">
          {subFiltersTitle ? (
            <p className="body2 text-flexcar-neutral60">{subFiltersTitle}</p>
          ) : null}

          {subFilters.map((subFilter) => {
            // Note: It's more performance to calculate checked at this level rather than
            // selectedSubFilters re-rendering FilterOptionSubCheckbox with every change.
            const checked =
              selectedSubFilters !== undefined && selectedSubFilters.includes(subFilter);

            return (
              <FilterOptionSubCheckbox
                key={subFilter}
                subFilter={subFilter}
                subFilterCount={
                  subFiltersCount !== undefined ? subFiltersCount[subFilter] : undefined
                }
                mainFilter={mainFilter}
                checked={checked}
                toggleSubCheckbox={toggleSubCheckbox}
              />
            );
          })}
        </FilterOptionCheckboxContainer>
      ) : null}
    </FilterOptionCheckboxContainer>
  );
}

// Note: Without this, all FilterOptionMainCheckboxes re-render if another is toggled.
export default React.memo(FilterOptionMainCheckbox);
